import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, MessageSquare, Mail, Phone, Ban, ShieldCheck, Briefcase } from 'lucide-react'
import { api } from '@/lib/api'
import { TagPicker } from '@/components/marketing/TagPicker'
import { CustomFieldsEditor } from '@/components/marketing/CustomFieldsEditor'
import { ActivityTimeline } from '@/components/marketing/ActivityTimeline'
import {
  STAGE_LABELS,
  serviceLineLabel,
  type PipelineStage,
  type ServiceLine,
} from '@/lib/stages'

interface ClientOpportunity {
  id: string
  title: string
  stage: PipelineStage
  serviceLine: string
  monetaryValueCents: number
  probability: number
  updatedAt: string
}

function eur(cents: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

interface ClientDetail {
  id: string
  displayName: string
  email: string | null
  phoneE164: string | null
  timezone: string
  source: string | null
  tags: string[]
  lastActivityAt: string | null
  dnd: boolean
  dndEmail: boolean
  dndSms: boolean
  dndWhatsapp: boolean
  dndVoice: boolean
  marketingConsentEmail: boolean
  marketingConsentSms: boolean
  marketingConsentWhatsapp: boolean
  clientTags: { id: string; tag: { id: string; name: string; color: string | null } }[]
  conversations: {
    id: string
    channelKind: string
    status: string
    lastMessageAt: string
    lastMessagePreview: string | null
    unreadCount: number
  }[]
  _count: { conversations: number; activityLog: number }
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [client, setClient] = useState<ClientDetail | null>(null)
  const [tab, setTab] = useState<'timeline' | 'conversations' | 'opportunities'>('timeline')
  const [opps, setOpps] = useState<ClientOpportunity[]>([])
  // Catálogo read-only de líneas de servicio (labels de los chips).
  const [serviceLines, setServiceLines] = useState<ServiceLine[]>([])

  async function load() {
    if (!id) return
    const c = await api<ClientDetail>(`/v1/marketing/clients/${id}`)
    setClient(c)
  }

  async function loadOpps() {
    if (!id) return
    try {
      const r = await api<{ items: ClientOpportunity[]; count: number }>(
        `/v1/marketing/opportunities?clientStubId=${id}&limit=100`,
      )
      setOpps(r.items)
    } catch {
      setOpps([])
    }
  }

  async function loadServiceLines() {
    try {
      const r = await api<{ items: ServiceLine[] }>(
        '/v1/marketing/service-lines?includeInactive=true',
      )
      setServiceLines(r.items)
    } catch {
      setServiceLines([])
    }
  }

  useEffect(() => {
    void load()
    void loadOpps()
    void loadServiceLines()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (!client) return <div className="p-6 text-sm font-mono text-ink-700/50">Cargando…</div>

  return (
    <div className="flex h-full">
      <div className="flex-1 overflow-auto">
        <header className="px-6 py-4 border-b border-ink-900/10 bg-white">
          <Link to="/marketing/clients" className="text-blueprint-700 text-xs flex items-center gap-1 mb-2">
            <ArrowLeft className="size-3" /> Clientes
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-medium">{client.displayName}</h1>
              <div className="flex items-center gap-3 text-xs font-mono text-ink-700/70 mt-1">
                {client.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="size-3" />
                    {client.email}
                  </span>
                )}
                {client.phoneE164 && (
                  <span className="flex items-center gap-1">
                    <Phone className="size-3" />
                    {client.phoneE164}
                  </span>
                )}
                <span>·</span>
                <span>{client.timezone}</span>
                {client.source && (
                  <>
                    <span>·</span>
                    <span>origen: {client.source}</span>
                  </>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/50">
                última actividad
              </div>
              <div className="text-xs font-mono text-ink-700">
                {client.lastActivityAt
                  ? new Date(client.lastActivityAt).toLocaleString('es-ES')
                  : '—'}
              </div>
            </div>
          </div>

          {(client.dnd ||
            client.dndEmail ||
            client.dndSms ||
            client.dndWhatsapp ||
            client.dndVoice ||
            client.marketingConsentEmail ||
            client.marketingConsentSms ||
            client.marketingConsentWhatsapp) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {client.dnd && (
                <span className="bp-pill bg-red-100 text-red-700">
                  <Ban className="size-3 mr-0.5 inline" /> DND global
                </span>
              )}
              {client.dndEmail && <span className="bp-pill bg-red-50 text-red-700">no email</span>}
              {client.dndSms && <span className="bp-pill bg-red-50 text-red-700">no sms</span>}
              {client.dndWhatsapp && <span className="bp-pill bg-red-50 text-red-700">no wa</span>}
              {client.dndVoice && <span className="bp-pill bg-red-50 text-red-700">no voice</span>}
              {client.marketingConsentEmail && (
                <span className="bp-pill bg-emerald-50 text-emerald-700">
                  <ShieldCheck className="size-3 mr-0.5 inline" /> email
                </span>
              )}
              {client.marketingConsentSms && (
                <span className="bp-pill bg-emerald-50 text-emerald-700">
                  <ShieldCheck className="size-3 mr-0.5 inline" /> sms
                </span>
              )}
              {client.marketingConsentWhatsapp && (
                <span className="bp-pill bg-emerald-50 text-emerald-700">
                  <ShieldCheck className="size-3 mr-0.5 inline" /> wa
                </span>
              )}
            </div>
          )}
        </header>

        <div className="px-6 pt-4">
          <div className="flex gap-1 border-b border-ink-900/10">
            <button
              onClick={() => setTab('timeline')}
              className={[
                'px-3 py-2 text-xs font-medium border-b-2 -mb-px',
                tab === 'timeline'
                  ? 'border-blueprint-600 text-blueprint-700'
                  : 'border-transparent text-ink-700/60 hover:text-ink-900',
              ].join(' ')}
            >
              Timeline ({client._count.activityLog})
            </button>
            <button
              onClick={() => setTab('conversations')}
              className={[
                'px-3 py-2 text-xs font-medium border-b-2 -mb-px',
                tab === 'conversations'
                  ? 'border-blueprint-600 text-blueprint-700'
                  : 'border-transparent text-ink-700/60 hover:text-ink-900',
              ].join(' ')}
            >
              Conversaciones ({client._count.conversations})
            </button>
            <button
              onClick={() => setTab('opportunities')}
              className={[
                'px-3 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1',
                tab === 'opportunities'
                  ? 'border-blueprint-600 text-blueprint-700'
                  : 'border-transparent text-ink-700/60 hover:text-ink-900',
              ].join(' ')}
            >
              <Briefcase className="size-3" />
              Oportunidades ({opps.length})
            </button>
          </div>
          <div className="py-4">
            {tab === 'timeline' && <ActivityTimeline clientStubId={client.id} />}
            {tab === 'opportunities' && (
              <ul className="space-y-1">
                {opps.length === 0 && (
                  <li className="text-xs text-ink-700/50 text-center py-6">
                    Sin oportunidades para este cliente.
                  </li>
                )}
                {opps.map((o) => (
                  <li key={o.id}>
                    <Link
                      to={`/marketing/opportunities/${o.id}`}
                      className="bp-card px-3 py-2 flex items-center gap-3 hover:bg-surface-alt"
                    >
                      <Briefcase className="size-3.5 text-blueprint-700" />
                      <span className="bp-pill text-[10px]">{STAGE_LABELS[o.stage]}</span>
                      <span className="bp-pill text-[10px]">
                        {serviceLineLabel(o.serviceLine, serviceLines)}
                      </span>
                      <span className="text-xs text-ink-900 truncate flex-1">{o.title}</span>
                      <span className="text-[11px] font-mono text-ink-700/70">
                        {eur(o.monetaryValueCents)}
                      </span>
                      <span className="text-[10px] font-mono text-ink-700/50">
                        {o.probability}%
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {tab === 'conversations' && (
              <ul className="space-y-1">
                {client.conversations.length === 0 && (
                  <li className="text-xs text-ink-700/50 text-center py-6">
                    Sin conversaciones.
                  </li>
                )}
                {client.conversations.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/marketing/inbox/${c.id}`}
                      className="bp-card px-3 py-2 flex items-center gap-3 hover:bg-surface-alt"
                    >
                      <MessageSquare className="size-3.5 text-blueprint-700" />
                      <span className="bp-pill">{c.channelKind}</span>
                      <span className="text-xs text-ink-900 truncate flex-1">
                        {c.lastMessagePreview ?? '(sin mensajes)'}
                      </span>
                      <span className="text-[10px] font-mono text-ink-700/50">
                        {new Date(c.lastMessageAt).toLocaleString('es-ES')}
                      </span>
                      {c.unreadCount > 0 && (
                        <span className="bp-pill bg-blueprint-600 text-white">
                          {c.unreadCount}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <aside className="w-72 shrink-0 border-l border-ink-900/10 bg-white overflow-auto">
        <div className="p-4 border-b border-ink-900/10">
          <TagPicker clientStubId={client.id} />
        </div>
        <div className="p-4">
          <CustomFieldsEditor clientStubId={client.id} />
        </div>
      </aside>
    </div>
  )
}
